import { Textarea as MantineTextarea, MantineSize } from "@mantine/core";
import { componentWrapper } from "../../utilities";
import { CustomTextareaFactory, CustomTextareaFormikFactory, IProps, ITextareaFormikProps } from "./textarea.types";
import styles from "./textarea.module.scss";
import inputStyles from "../input/input.module.scss";
import { useField } from "formik";
import { Skeleton } from "../skeleton";
import { IProps as ISkeletonProps } from "../skeleton/skeleton.types";
import classnames from "classnames";

/**
 * Textarea component for multi-line text input.
 * @returns {JSX.Element} The Textarea component.
 */
const Textarea = componentWrapper<CustomTextareaFactory, IProps>("Textarea", ({ ...otherProps }, ref) => {
    return (
        <MantineTextarea
            ref={ref}
            classNames={{
                label: inputStyles["label"],
                section: inputStyles["section"],
                wrapper: inputStyles["wrapper"],
                input: `${inputStyles["input"]} ${styles["input"]}`,
            }}
            {...otherProps}
        />
    );
});

/**
 * Textarea component integrated with Formik for form handling and validation.
 * @param {string} props.name - The name of the textarea field for Formik.
 * @param {function} props.validate - Custom validation function for the field.
 * @returns {JSX.Element} The TextareaFormik component.
 */
const TextareaFormik = componentWrapper<CustomTextareaFormikFactory, ITextareaFormikProps>(
    "TextareaFormik",
    ({ name, validate, ...otherProps }, ref) => {
        const [field, meta] = useField({ name, validate });
        return (
            <Textarea
                ref={ref}
                classNames={{ root: inputStyles["root"], input: styles["input"], label: styles["label"] }}
                {...otherProps}
                error={meta.touched ? meta.error : undefined}
                {...field}
            />
        );
    }
);

Textarea.Formik = TextareaFormik;

/**
 * Textarea Skeleton component.
 */
const TextareaSkeleton = ({
    size = "sm",
    rows = 2,
    className,
    visible,
    ...props
}: ISkeletonProps & { size?: MantineSize; rows?: number }) => {
    return (
        <Skeleton
            visible={visible}
            radius="md"
            data-size={size}
            data-rows={visible !== false ? rows : undefined}
            className={classnames(styles["skeleton"], className)}
            {...props}
        />
    );
};
Textarea.Skeleton = TextareaSkeleton;

export { Textarea, TextareaFormik, TextareaSkeleton };
